import {calculateErrorMessage} from 'common/helpers'
import {useCallback, useMemo, useRef} from 'react'

import {VoiceCallError} from './useVoiceCallState'
import {VoiceCallSharedRefs} from './useVoiceCallStateRegistry'

/*
 * NO TRANSCRIPTION HAPPENS IN THE BROWSER.
 *
 * This hook used to run the Web Speech API alongside the mic capture. Its text
 * was never committed to the transcript — the comment that used to sit below
 * said so — because it decodes in ONE fixed `lang` taken from the UI language
 * and cannot detect another, so Urdu spoken under an English UI came out as
 * English-looking nonsense. It survived as a voice-activity and interrupt
 * signal, and as the last place in the product that guessed a language from
 * the UI.
 *
 * Both jobs are done by the AUDIO LEVEL, which is what this hook measures
 * anyway and what is actually true of a person speaking. The utterance is
 * recorded and POSTed, and the server transcribes it with no hint from us.
 *
 * WHAT CHANGED FOR THE USER: barge-in is now "the user started speaking"
 * rather than "the user said one of these keywords". A keyword list could only
 * ever interrupt in the language it was written in.
 */

export const useVoiceCallVAD = (
  refs: VoiceCallSharedRefs,
  stateActions: {
    setMicLevel: (l: number) => void
    setError: (e: VoiceCallError | null) => void
    setInterimUserTranscript: (t: string) => void
  },
  callbacks: {
    handleUserInterruptAndRestart: (t?: string) => void
    userTranscriptExists: (t: string) => boolean
    commitTranscript: (t: string, s: 'user' | 'ai', opts?: {provisional?: boolean}) => void
    shouldProcessListening: () => boolean
  }
) => {
  const {setMicLevel, setError, setInterimUserTranscript} = stateActions

  const micStreamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  const stopMicCapture = useCallback(() => {
    try {
      const gw = window as any
      if (micStreamRef.current) {
        try {
          micStreamRef.current.getTracks().forEach((track) => track.stop())
        } catch (err) {
          calculateErrorMessage(err)
        }
        try {
          gw.__activeVoiceMediaStreams?.delete(micStreamRef.current)
        } catch (err) {
          calculateErrorMessage(err)
        }
        micStreamRef.current = null
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close()
        } catch (e) {
          calculateErrorMessage(e)
        }
        try {
          gw.__activeVoiceAudioContexts?.delete(audioContextRef.current)
        } catch (err) {
          calculateErrorMessage(err)
        }
        audioContextRef.current = null
      }
    } catch (err) {
      calculateErrorMessage(err)
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    setMicLevel(0)
  }, [setMicLevel])

  const startMicCapture = useCallback(async () => {
    // Only acquire the microphone when a voice call is active and walkthrough is not active
    try {
      const status = refs.statusRef.current
      if (refs.walkthroughActiveRef.current) return false
      const allowedStatuses: Array<string> = ['connecting', 'listening', 'choosing_voice']
      if (!allowedStatuses.includes(status)) return false
      if (refs.mutedRef.current) return false

      const stream = await navigator.mediaDevices.getUserMedia({audio: true})
      micStreamRef.current = stream
      try {
        refs.permissionDeniedRef.current = false
      } catch (e) {
        calculateErrorMessage(e)
      }

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      audioContextRef.current = audioContext
      try {
        const gw = window as any
        gw.__activeVoiceMediaStreams = gw.__activeVoiceMediaStreams || new Set()
        gw.__activeVoiceAudioContexts = gw.__activeVoiceAudioContexts || new Set()
        gw.__activeVoiceMediaStreams.add(micStreamRef.current)
        gw.__activeVoiceAudioContexts.add(audioContextRef.current)
      } catch (err) {
        calculateErrorMessage(err)
      }
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048
      analyser.smoothingTimeConstant = 0.8

      source.connect(analyser)
      analyserRef.current = analyser
      const timeDomainArray = new Uint8Array(analyser.fftSize)

      let lastUpdateTime = 0
      const frameInterval = 1000 / 60

      const updateLevel = () => {
        const now = performance.now()
        if (now - lastUpdateTime >= frameInterval) {
          if (analyserRef.current) {
            analyserRef.current.getByteTimeDomainData(timeDomainArray)
            let sumSquares = 0
            for (let i = 0; i < timeDomainArray.length; i++) {
              const centered = (timeDomainArray[i] - 128) / 128
              sumSquares += centered * centered
            }
            const rms = Math.sqrt(sumSquares / timeDomainArray.length)
            setMicLevel(rms)

            const shouldProcess = callbacks.shouldProcessListening()

            if (shouldProcess) {
              refs.noiseFloorRef.current = Math.min(
                0.1,
                refs.noiseFloorRef.current * 0.99 + rms * 0.01
              )
              const NOISE_MULTIPLIER = 5.0
              const MIN_SPEECH_THRESHOLD = 0.035
              const MAX_SPEECH_THRESHOLD = 0.4

              const dynamicThreshold = Math.max(
                MIN_SPEECH_THRESHOLD,
                Math.min(MAX_SPEECH_THRESHOLD, refs.noiseFloorRef.current * NOISE_MULTIPLIER)
              )

              let effectiveThreshold = dynamicThreshold
              if (Date.now() - refs.noActivityTimeRef.current > 10000) {
                effectiveThreshold = Math.max(MIN_SPEECH_THRESHOLD, dynamicThreshold * 0.8)
              }

              refs.voiceActivityBufferRef.current.push(rms > effectiveThreshold ? 1 : 0)
              if (refs.voiceActivityBufferRef.current.length > 12) {
                refs.voiceActivityBufferRef.current.shift()
              }

              const recentActivity = refs.voiceActivityBufferRef.current.reduce((a, b) => a + b, 0)
              const sustainedSpeechThreshold = 5
              const currentTime = Date.now()

              if (recentActivity >= sustainedSpeechThreshold) {
                if (!refs.sustainedSpeechRef.current) {
                  refs.sustainedSpeechRef.current = true
                  refs.speechStartTimeRef.current = currentTime
                  refs.hasVoiceActivityRef.current = true
                }
                if (refs.realSpeechDetectedRef.current || rms > effectiveThreshold * 2) {
                  refs.lastAudioActivityRef.current = currentTime
                }

                if (rms > effectiveThreshold * 2) {
                  refs.consecutiveRealSpeechRef.current += 1
                  if (refs.consecutiveRealSpeechRef.current >= 5) {
                    refs.realSpeechDetectedRef.current = true
                  }
                } else {
                  refs.consecutiveRealSpeechRef.current = 0
                }
              } else if (refs.sustainedSpeechRef.current && recentActivity < 3) {
                refs.sustainedSpeechRef.current = false
              }

              if (recentActivity >= sustainedSpeechThreshold) {
                refs.noActivityTimeRef.current = Date.now()
              }

              const maxTime = refs.realSpeechDetectedRef.current ? 60000 : 90000
              const listeningDuration = currentTime - refs.maxListeningTimeRef.current
              if (listeningDuration > maxTime && !refs.responseRequestedRef.current) {
                if (
                  (refs.hasVoiceActivityRef.current || refs.realSpeechDetectedRef.current) &&
                  !refs.responseRequestedRef.current
                ) {
                  refs.sustainedSpeechRef.current = false
                  refs.realSpeechDetectedRef.current = false
                  refs.voiceActivityBufferRef.current = []
                  refs.maxListeningTimeRef.current = currentTime
                }
              }
            }
            lastUpdateTime = now
          }
        }
        animationFrameRef.current = requestAnimationFrame(updateLevel)
      }
      updateLevel()
      return true
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        try {
          refs.permissionDeniedRef.current = true
        } catch (e) {
          calculateErrorMessage(e)
        }
        setError({
          type: 'permission_denied',
          message: 'Microphone permission was denied',
          retryable: false,
        })
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError({
          type: 'microphone_not_accessible',
          message: 'No microphone device found',
          retryable: false,
        })
      } else {
        setError({
          type: 'unknown',
          message: err.message || 'Failed to access microphone',
          retryable: true,
        })
      }
      return false
    }
  }, [setMicLevel, callbacks, refs, setError])

  const startListening = useCallback(() => {
    // The mic analyser is already running; all this has to do is clear the
    // caption line so the last turn's text is not left under a live mic.
    setInterimUserTranscript('')
  }, [setInterimUserTranscript])

  const stopListening = useCallback(() => {
    setInterimUserTranscript('')
  }, [setInterimUserTranscript])

  return useMemo(
    () => ({
      refs: {
        micStreamRef,
      },
      startMicCapture,
      stopMicCapture,
      startListening,
      stopListening,
    }),
    [startMicCapture, stopMicCapture, startListening, stopListening]
  )
}
